import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, RefreshCw, FileText, Link2, Download } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/psg`;

const STATUS = {
  A_IMPORTER: { label: 'À IMPORTER', cls: 'bg-emerald-500/20 text-emerald-400' },
  VALIDATION: { label: 'VALIDATION REQUISE', cls: 'bg-orange-500/20 text-orange-400' },
  DEJA_PRESENT: { label: 'DÉJÀ DANS LE MANAGER', cls: 'bg-sky-500/20 text-sky-400' },
};
const KINDS = ['TOUS', 'PRÊT ABO', 'ACHAT', 'TRANSFERT', 'REVENTE', 'ABONNEMENT', 'AUTRE'];

const Chip = ({ active, onClick, children, testid }) => (
  <button
    onClick={onClick}
    data-testid={testid}
    className={`px-2 py-1 rounded text-xs font-medium border transition-colors ${active ? 'bg-[#C4A052] text-black border-[#C4A052]' : 'bg-zinc-800 text-zinc-300 border-zinc-700 hover:border-[#C4A052]/50'}`}
  >
    {children}
  </button>
);

const fmtDate = (d) => (d ? d.split('-').reverse().join('/') : '-');

const TicketRow = ({ row, checked, onToggle }) => {
  const { it, t } = row;
  const st = STATUS[t.status] || STATUS.VALIDATION;
  return (
    <div className="flex items-start gap-3 p-3 rounded-lg border border-zinc-800 bg-zinc-900/60 hover:border-[#C4A052]/40 transition-colors" data-testid={`psg-row-${t.tkey}`}>
      <Checkbox checked={checked} onCheckedChange={onToggle} disabled={t.status === 'DEJA_PRESENT'} data-testid={`psg-select-${t.tkey}`} className="mt-1" />
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-bold text-white text-sm">{t.match || it.match || 'PSG'}</span>
          <span className="text-xs text-zinc-400">{fmtDate(t.date)} {t.heure}</span>
          <span className="text-xs px-2 py-0.5 rounded bg-zinc-800 text-[#C4A052]">{it.kind}</span>
          <span className={`text-xs px-2 py-0.5 rounded-full font-bold ${st.cls}`} data-testid={`psg-status-${t.tkey}`}>{st.label}</span>
        </div>
        <p className="text-sm text-zinc-200 mt-1" data-testid={`psg-seat-${t.tkey}`}>
          Tribune <b>{t.tribune || '?'}</b> · Accès <b>{t.acces || '?'}</b> · Porte <b>{t.porte || '?'}</b> · Rang <b>{t.rang || '?'}</b> · Place <b>{t.place || '?'}</b>
        </p>
        <p className="text-xs text-zinc-500 mt-0.5 truncate">
          {t.titulaire ? `Titulaire ${t.titulaire} · ` : ''}{it.preteur ? `Prêté par ${it.preteur} · ` : ''}Cde {t.commande || '?'} · {it.to}
        </p>
        {(t.issues || []).length > 0 && <p className="text-xs text-orange-400 mt-0.5">{t.issues.join(' · ')}</p>}
      </div>
      <div className="flex flex-col gap-1 items-end">
        <a href={`${API}/pdf/${encodeURIComponent(t.tkey)}`} target="_blank" rel="noreferrer" data-testid={`psg-pdf-${t.tkey}`} className="inline-flex items-center gap-1 text-xs text-[#D4AF37] hover:underline">
          <FileText className="h-3 w-3" /> PDF
        </a>
        {t.lien && (
          <a href={t.lien} target="_blank" rel="noreferrer" data-testid={`psg-link-${t.tkey}`} className="inline-flex items-center gap-1 text-xs text-violet-400 hover:underline">
            <Link2 className="h-3 w-3" /> Lien
          </a>
        )}
      </div>
    </div>
  );
};

export const PsgMailModal = ({ open, onOpenChange, onImported }) => {
  const [data, setData] = useState({ items: [], counts: {}, job: {} });
  const [kind, setKind] = useState('TOUS');
  const [statusF, setStatusF] = useState('TOUS');
  const [selected, setSelected] = useState(new Set());
  const [importing, setImporting] = useState(false);

  const load = useCallback(async () => {
    const r = await axios.get(`${API}/status`);
    setData(r.data);
    return r.data;
  }, []);

  useEffect(() => { if (open) load(); }, [open, load]);

  const running = data.job && data.job.status === 'running';
  useEffect(() => {
    if (!running) return undefined;
    const t = setInterval(load, 2000);
    return () => clearInterval(t);
  }, [running, load]);

  const scan = async (force = false) => {
    await axios.post(`${API}/scan`, null, { params: { force } });
    toast.info('Scan PSG lancé (tout l\'historique)');
    load();
  };

  const rows = useMemo(() => data.items.flatMap((it) => it.tickets.map((t) => ({ it, t }))), [data.items]);
  const shown = rows.filter(({ it, t }) => (kind === 'TOUS' || it.kind === kind) && (statusF === 'TOUS' || t.status === statusF));
  const noTicket = data.items.filter((it) => !it.tickets.length && !it.doublon && (kind === 'TOUS' || it.kind === kind));

  const toggle = (k) => setSelected((s) => { const n = new Set(s); n.has(k) ? n.delete(k) : n.add(k); return n; });
  const selectSafe = () => setSelected(new Set(shown.filter(({ t }) => t.status === 'A_IMPORTER').map(({ t }) => t.tkey)));

  const doImport = async () => {
    setImporting(true);
    try {
      const r = await axios.post(`${API}/import`, { tkeys: [...selected] }, { timeout: 300000 });
      const d = r.data;
      toast.success(`PSG : ${d.created} billet(s) importé(s), ${d.updated} mis à jour, ${d.duplicates} déjà présent(s)`);
      if (d.errors.length) toast.error(d.errors.slice(0, 3).join(' | '));
      setSelected(new Set());
      await load();
      if (onImported) onImported();
    } catch (e) {
      toast.error(`Import PSG impossible : ${e.message}`);
    } finally {
      setImporting(false);
    }
  };

  const job = data.job || {};
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto overflow-x-hidden bg-[#121212] border-[#C4A052]/30" data-testid="psg-modal">
        <DialogHeader>
          <DialogTitle className="text-[#C4A052]">PSG — e-billets depuis la Boîte Mail</DialogTitle>
          <DialogDescription className="text-zinc-400">
            Tous les mails PSG (toutes les boîtes, tout l&apos;historique). Le PDF est téléchargé et l&apos;emplacement lu dans le billet. Projet : PSG · prix 0 €.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <Button onClick={() => scan(false)} disabled={running} className="bg-[#C4A052] text-black hover:bg-[#d4b062]" data-testid="psg-scan-btn">
              {running ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
              {running ? `Scan… ${job.seen || 0} mails PSG vus, ${job.processed || 0} lus` : 'SCANNER LES MAILS PSG'}
            </Button>
            <Button variant="outline" onClick={() => scan(true)} disabled={running} className="border-zinc-700" data-testid="psg-rescan-btn">Re-scan complet</Button>
            {job.finished_at && !running && <span className="text-xs text-zinc-500" data-testid="psg-last-scan">Dernier scan : {new Date(job.finished_at).toLocaleString('fr-FR')}</span>}
            {(job.errors || []).length > 0 && <span className="text-xs text-red-400">{job.errors.join(' | ')}</span>}
          </div>

          <div className="flex flex-wrap gap-1" data-testid="psg-kind-filters">
            {KINDS.map((k) => (
              <Chip key={k} active={kind === k} onClick={() => setKind(k)} testid={`psg-kind-${k}`}>
                {k} ({k === 'TOUS' ? rows.length : rows.filter(({ it }) => it.kind === k).length})
              </Chip>
            ))}
          </div>
          <div className="flex flex-wrap gap-1" data-testid="psg-status-filters">
            {['TOUS', ...Object.keys(STATUS)].map((k) => (
              <Chip key={k} active={statusF === k} onClick={() => setStatusF(k)} testid={`psg-status-filter-${k.toLowerCase()}`}>
                {k === 'TOUS' ? 'Tous' : STATUS[k].label} ({k === 'TOUS' ? rows.length : data.counts[k] || 0})
              </Chip>
            ))}
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <Button size="sm" variant="outline" onClick={selectSafe} className="border-zinc-700" data-testid="psg-select-safe-btn">Sélectionner les « À IMPORTER »</Button>
            <Button size="sm" onClick={doImport} disabled={!selected.size || importing} className="bg-emerald-600 hover:bg-emerald-500 text-white" data-testid="psg-import-btn">
              {importing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
              IMPORTER {selected.size} BILLET(S) → PSG
            </Button>
          </div>

          <div className="space-y-2" data-testid="psg-list">
            {shown.map((row) => (
              <TicketRow key={row.t.tkey} row={row} checked={selected.has(row.t.tkey)} onToggle={() => toggle(row.t.tkey)} />
            ))}
            {!shown.length && !running && <p className="text-sm text-zinc-500 py-6 text-center" data-testid="psg-empty">Aucun billet PSG. Lancez le scan.</p>}
          </div>

          {noTicket.length > 0 && statusF === 'TOUS' && (
            <details className="text-xs text-zinc-500" data-testid="psg-no-ticket">
              <summary className="cursor-pointer">{noTicket.length} mail(s) PSG sans e-billet lisible</summary>
              {noTicket.map((it) => (
                <p key={it.key} className="mt-1 truncate">{(it.date || '').slice(0, 10)} · {it.kind} · {it.subject} {it.issues.length ? `— ${it.issues.join(' · ')}` : ''}</p>
              ))}
            </details>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
